import React, { useState, useEffect, useCallback } from 'react';
import { filter } from 'lodash';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import {
  Box,
  Card,
  Table,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  tableCellClasses
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import usePayoutSelector from '../../store/Slice/Payout/PayoutSelector';
import { getNetworkList } from '../../store/Slice/Payout/PayoutSlice';
import useDebounce from '../../utils/useDebounce';
import NetworkToolbar from './NetworkToolbar';
import { PayoutApi } from '../../utils/Apis';
import { downloadApiData } from '../../store/Slice/Global/GlobalSlice';
import { normalizeValue } from '../../utils/CommonFunction';
import { NOT_AVAILABLE } from '../Global/Constants';
import SimplePagination from '../../components/SimplePagination';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'identifier', label: 'Identifier', alignRight: false },
  { id: 'reportingManagerName', label: 'Reporting Manager', alignRight: false },
  { id: 'salesManagerName', label: 'Sales Manager', alignRight: false },
  { id: 'type', label: 'Type', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'yearsOfRelationship', label: 'Years Of Relationship', alignRight: false },
  { id: 'netSalesYTD', label: 'Net Sales YTD', alignRight: false },
  { id: 'createdOn', label: 'Created On', alignRight: false },
  { id: 'action', label: 'Action', alignRight: false, align: 'center' }
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) =>
        (_user.name && _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user.identifier &&
          String(_user.identifier).toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user.reportingManagerName &&
          _user.reportingManagerName.toLowerCase().indexOf(query.toLowerCase()) !== -1)
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 13,
    fontWeight: 'bold',
    padding: '8px 12px',
    backgroundColor: '#ffffff',
    color: '#13519d'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: '8px 12px'
  }
}));

export default function Network() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage] = useState(20);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { PayoutNetworkData } = usePayoutSelector();
  const [pageMenu] = useState([]);
  const [filterState, setFilterState] = useState({
    typeId: null,
    isActive: null,
    cityId: null
  });

  const getNetworkDataList = useCallback(() => {
    if (!LoginData?.userId) return;
    const credential = {
      userId: LoginData?.userId,
      page: page + 1,
      size: rowsPerPage
    };
    dispatch(getNetworkList(credential));
  }, [LoginData, dispatch, page, rowsPerPage]);

  useEffect(() => {
    getNetworkDataList();
  }, [getNetworkDataList]);

  const PayoutNetworkList = PayoutNetworkData?.summary?.items || [];
  const hasNextPage = PayoutNetworkList.length >= rowsPerPage;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = PayoutNetworkList.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleDownload = async () => {
    const credentials = {
      userId: LoginData?.userId,
      reporteeIds: selected
    };

    try {
      const result = await dispatch(downloadApiData(PayoutApi?.payoutNetworkDownload, credentials));

      if (result?.success && result?.data) {
        const fileName = `Network_Data_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.xlsx`;
        const url = window.URL.createObjectURL(result.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Download failed:', result?.error);
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const filteredUsers = applySortFilter(
    PayoutNetworkList,
    getComparator(order, orderBy),
    filterName
  );
  const isUserNotFound = filteredUsers.length === 0;

  return (
    <Box sx={{ width: '100%' }}>
      <NetworkToolbar
        numSelected={selected.length}
        filterName={filterName}
        onFilterName={handleFilterByName}
        parent="network"
        pageMenu={pageMenu}
        filterState={filterState}
        setFilterState={setFilterState}
        setFilterName={setFilterName}
      />

      <Scrollbar>
        <TableContainer sx={{ minWidth: 800, overflow: 'auto' }}>
          <Table>
            <UserListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={PayoutNetworkList.length}
              numSelected={selected.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
              isCheckBox="true"
            />
            <TableBody>
              {filteredUsers.map((row, index) => {
                const {
                  id,
                  identifier,
                  name,
                  netSalesYTD,
                  reportingManagerName,
                  salesManagerName,
                  yearsOfRelationship,
                  status,
                  type,
                  createdOn
                } = row;
                const itemKey = id || index;
                const isItemSelected = selected.indexOf(id) !== -1;

                return (
                  <TableRow
                    hover
                    key={itemKey}
                    tabIndex={-1}
                    role="checkbox"
                    selected={isItemSelected}
                    aria-checked={isItemSelected}
                    sx={{
                      '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                      '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: 'rgba(19, 81, 157, 0.08)'
                      }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={(event) => handleClick(event, id)}
                        sx={{
                          color: '#94a3b8',
                          '&.Mui-checked': { color: '#13519d' }
                        }}
                      />
                    </TableCell>

                    <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#13519d' }}>
                      {name || NOT_AVAILABLE}
                    </StyledTableCell>
                    <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#13519d' }}>
                      {identifier || NOT_AVAILABLE}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {reportingManagerName || NOT_AVAILABLE}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {salesManagerName || NOT_AVAILABLE}
                    </StyledTableCell>
                    <StyledTableCell align="left">{type || NOT_AVAILABLE}</StyledTableCell>
                    <StyledTableCell align="left">
                      <Label
                        variant="ghost"
                        color={
                          status === 'Active'
                            ? 'success'
                            : status === 'Inactive'
                            ? 'error'
                            : 'default'
                        }
                      >
                        {status || NOT_AVAILABLE}
                      </Label>
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {normalizeValue(yearsOfRelationship, NOT_AVAILABLE)}
                    </StyledTableCell>
                    <StyledTableCell align="left" sx={{ fontWeight: 700 }}>
                      {normalizeValue(netSalesYTD, NOT_AVAILABLE)}
                    </StyledTableCell>
                    <StyledTableCell align="left">{createdOn || NOT_AVAILABLE}</StyledTableCell>

                    <StyledTableCell align="center" width={80}>
                      <UserMoreMenu rowId={row} parent="network" />
                    </StyledTableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            {isUserNotFound && (
              <TableBody>
                <TableRow>
                  <StyledTableCell align="center" colSpan={TABLE_HEAD.length + 1} sx={{ py: 3 }}>
                    <SearchNotFound searchQuery={filterName} />
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
      <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
    </Box>
  );
}
